import { useEffect, useMemo, useState } from "react";
import { Check, Clapperboard, Film, Loader2, Play, Scissors, Sparkles, Video } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ServiceFaqSection } from "@/components/ServiceFaqSection";
import { toast } from "sonner";

const SERVICE_TYPES = [
  "Music Video",
  "Vlogging Day",
  "Event Filming",
  "Brand Advert",
  "Short-Form Content (Reels/TikTok)",
  "Video Editing Only",
];

const PACKAGES = [
  {
    name: "Music Video",
    price: "From $750",
    icon: Film,
    blurb: "Full concept to final cut. Treatment, shoot day, cinematic edit + color grade.",
    bullets: ["½-day shoot", "Cinema-grade color", "Up to 4-min edit", "Vertical cutdowns"],
    service_type: "Music Video",
  },
  {
    name: "Short-Form Reel",
    price: "From $150",
    icon: Sparkles,
    blurb: "IG Reels, TikTok and Shorts built to loop and hook in the first second.",
    bullets: ["Up to 60 sec", "Captions + motion", "3 platform exports", "48-hr turnaround"],
    service_type: "Short-Form Content (Reels/TikTok)",
  },
  {
    name: "Event Filming",
    price: "From $400",
    icon: Video,
    blurb: "Shows, release parties, pop-ups — multicam coverage with a sizzle recap.",
    bullets: ["2-hr coverage", "Multicam setup", "90-sec recap", "Full-length cut"],
    service_type: "Event Filming",
  },
  {
    name: "Brand Advert",
    price: "From $500",
    icon: Clapperboard,
    blurb: "Product spots and brand films that look like they cost double what they did.",
    bullets: ["Concept + script", "Studio or on-location", "30 / 60 sec cuts", "Logo + CTA pack"],
    service_type: "Brand Advert",
  },
  {
    name: "Vlogging Day",
    price: "From $300",
    icon: Play,
    blurb: "A full day in your world — b-roll, interview beats, rough cut ready to post.",
    bullets: ["Up to 6 hours", "b-roll + audio", "Rough-cut delivered", "Thumbnail frame"],
    service_type: "Vlogging Day",
  },
  {
    name: "Video Editing Only",
    price: "From $100",
    icon: Scissors,
    blurb: "You bring the footage, I bring the pace, color and sound design.",
    bullets: ["Per-minute pricing", "Color + sound mix", "Motion titles", "2 revision rounds"],
    service_type: "Video Editing Only",
  },
];

function YouTubeEmbed({ id, title }) {
  const [playing, setPlaying] = useState(false);
  if (!id) return null;
  const poster = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
  const posterFb = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  return (
    <div
      className="group relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black"
      data-testid={`videography-youtube-${id}`}
    >
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={title || "YouTube video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group/play block h-full w-full"
          data-testid={`videography-youtube-play-${id}`}
        >
          <img
            src={poster}
            onError={(e) => {
              e.currentTarget.src = posterFb;
            }}
            alt={title || "Video poster"}
            className="h-full w-full object-cover transition-transform duration-700 group-hover/play:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-300 text-black shadow-[0_0_40px_rgba(110,231,183,0.35)] transition-transform group-hover/play:scale-110">
              <Play size={22} className="ml-1" fill="currentColor" />
            </span>
          </span>
          {title && (
            <span className="absolute inset-x-0 bottom-0 p-4 text-left">
              <span className="font-heading text-sm font-bold text-white">{title}</span>
            </span>
          )}
        </button>
      )}
    </div>
  );
}

export default function Videography() {
  const [videos, setVideos] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: SERVICE_TYPES[0],
    project_title: "",
    description: "",
    references: "",
    budget: "",
    deadline: "",
    location: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/videography/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  const pickTier = (service_type) => {
    setForm((f) => ({ ...f, service_type }));
    document
      .getElementById("videography-intake")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/videography/intake", form);
      setSubmitted(true);
      toast.success("Enquiry received — I'll be in touch shortly");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Could not submit — try again");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500";

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="videography-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Lens &amp; motion work</p>
        <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Videography
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-zinc-400 sm:text-base">
          Music videos, brand films, event coverage, short-form that actually hooks — shot and cut
          out of Atlanta. Peek at recent releases below, pick a package, then drop the brief.
        </p>
      </Reveal>

      {/* Released Videos */}
      <section className="mt-14" data-testid="videography-videos">
        <Reveal>
          <div className="flex items-center gap-3">
            <Film size={16} className="text-emerald-300" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Recent Releases
            </h2>
          </div>
          <p className="mt-2 max-w-xl text-xs text-zinc-500">
            A look at videos I've shot and cut — press play.
          </p>
        </Reveal>
        {videos.length > 0 ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="videography-video-grid">
            {videos.map((v, i) => (
              <Reveal key={v.id} delay={(i % 2) * 0.08}>
                <YouTubeEmbed id={v.youtube_id} title={v.title} />
              </Reveal>
            ))}
          </div>
        ) : (
          <p
            className="mt-8 rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600"
            data-testid="videography-videos-empty"
          >
            New releases drop here soon — check back.
          </p>
        )}
      </section>

      {/* Packages */}
      <section className="mt-20" data-testid="videography-packages">
        <Reveal>
          <div className="flex items-center gap-3">
            <Clapperboard size={16} className="text-emerald-300" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Starter Packages
            </h2>
          </div>
          <p className="mt-2 max-w-xl text-xs text-zinc-500">
            Starting-rate packages — every brief gets a real quote once we jump on the details.
          </p>
        </Reveal>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="videography-package-grid">
          {PACKAGES.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.name} delay={(i % 3) * 0.06}>
                <div
                  className="group relative flex h-full flex-col rounded-2xl border border-white/10 bg-[#141419] p-6 transition-colors hover:border-emerald-300/40"
                  data-testid={`videography-package-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-300/10 text-emerald-300">
                      <Icon size={18} />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-200">
                      {p.price}
                    </span>
                  </div>
                  <h3 className="font-heading mt-5 text-xl font-extrabold tracking-tight text-white">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-xs text-zinc-400">{p.blurb}</p>
                  <ul className="mt-4 space-y-1.5 text-xs text-zinc-300">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <Check size={12} className="mt-0.5 shrink-0 text-emerald-300" /> {b}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => pickTier(p.service_type)}
                    data-testid={`videography-pick-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-6 flex items-center justify-center gap-2 rounded-full border border-emerald-300/40 bg-emerald-300/5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-emerald-200 transition-all hover:bg-emerald-300 hover:text-black"
                  >
                    Request Quote
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Intake Form */}
      <section className="mt-20 scroll-mt-24" id="videography-intake" data-testid="videography-intake-section">
        <Reveal>
          <div className="flex items-center gap-3">
            <Video size={16} className="text-emerald-300" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Project Intake
            </h2>
          </div>
          <p className="mt-2 max-w-lg text-sm text-zinc-400">
            Tell me what you're building. I'll come back within 24 hours with a tailored quote and
            a schedule.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          {submitted ? (
            <div
              className="mt-8 rounded-3xl border border-emerald-300/40 bg-emerald-300/5 p-10 text-center"
              data-testid="videography-intake-success"
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-300/15 text-emerald-300">
                <Check size={28} />
              </span>
              <h3 className="font-heading mt-5 text-2xl font-extrabold tracking-tight text-white">
                Brief received
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm text-zinc-400">
                Appreciate you. I'll be in touch at{" "}
                <span className="text-emerald-200">{form.email}</span> within 24 hours with a quote
                and next steps.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setForm((f) => ({ ...f, project_title: "", description: "", references: "", budget: "", deadline: "", location: "", notes: "" }));
                }}
                data-testid="videography-intake-reset"
                className="mt-6 rounded-full border border-white/15 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:text-emerald-200"
              >
                Submit another brief
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-8 rounded-3xl border border-white/10 bg-[#141419] p-6 sm:p-10"
              data-testid="videography-intake-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>Your name *</span>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    placeholder="Artist / brand name"
                    data-testid="videography-intake-name"
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Email *</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    placeholder="you@email.com"
                    data-testid="videography-intake-email"
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Phone</span>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                    placeholder="(404) 555-0123"
                    data-testid="videography-intake-phone"
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Service type *</span>
                  <select
                    value={form.service_type}
                    onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                    className={inputCls}
                    data-testid="videography-intake-service"
                  >
                    {SERVICE_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className={labelCls}>Project title</span>
                  <input
                    value={form.project_title}
                    onChange={(e) => setForm({ ...form, project_title: e.target.value })}
                    className={inputCls}
                    placeholder='e.g. "Midnight Heat — music video"'
                    data-testid="videography-intake-title"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className={labelCls}>Project brief *</span>
                  <textarea
                    required
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className={inputCls}
                    placeholder="What's the concept, mood, deliverables? Where are we shooting?"
                    data-testid="videography-intake-description"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className={labelCls}>Reference links / moodboard</span>
                  <textarea
                    rows={2}
                    value={form.references}
                    onChange={(e) => setForm({ ...form, references: e.target.value })}
                    className={inputCls}
                    placeholder="YouTube links, IG reels, Pinterest boards…"
                    data-testid="videography-intake-references"
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Budget range</span>
                  <input
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    className={inputCls}
                    placeholder="e.g. $500 – $1,000"
                    data-testid="videography-intake-budget"
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Deadline</span>
                  <input
                    type="date"
                    min={todayIso}
                    value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    className={inputCls}
                    data-testid="videography-intake-deadline"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className={labelCls}>Location</span>
                  <input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className={inputCls}
                    placeholder="Atlanta, remote, on-location, your spot…"
                    data-testid="videography-intake-location"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className={labelCls}>Anything else</span>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className={inputCls}
                    placeholder="Cast count, props, delivery format, platforms you post to…"
                    data-testid="videography-intake-notes"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="videography-intake-submit"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-4 text-xs font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_30px_rgba(110,231,183,0.3)] disabled:opacity-50"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Video size={15} />}
                Send Brief — Get a Quote
              </button>
              <p className="mt-3 text-center text-[11px] text-zinc-600">
                No deposit now — I'll confirm the scope and price first.
              </p>
            </form>
          )}
        </Reveal>
      </section>

      <ServiceFaqSection
        matchTitles={["Music Video", "Vlogging"]}
        heading="Videography FAQ"
        eyebrow="Before you brief"
        blurb="Answers to the questions that come up most often on video projects."
        testid="videography-faq"
      />
    </div>
  );
}
